import React, { useState, useEffect } from 'react';
import "./Admin.scss";
import "./Adaptations.scss";
import Switch2 from '../../components/switch/Switch2';
import { fetchBlocks, fetchPlots } from '../../utils/api';
import Logo from "../../static/images/AlZumurudaLogo.png"

const SESSION_DURATION = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

const checkSession = () => {
    const sessionData = localStorage.getItem('adminSession');
    if (!sessionData) return null;

    const { token, expiry } = JSON.parse(sessionData);
    if (Date.now() > expiry) {
        localStorage.removeItem('adminSession');
        return null;
    }
    return token;
};

const UNIT_STATUSES = ['available', 'sold', 'not_available'];

export default function Admin() {
    const [switchState, setSwitchState] = useState(true);
    const [plotData, setPlotData] = useState([]);
    const [blockData, setBlockData] = useState([]);
    const [items, setItems] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: '#',
        direction: 'asc'
    });
    const [filterActive, setFilterActive] = useState(false);
    const [filters, setFilters] = useState({
        status: [],
        zone: []
    });
    

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [token, setToken] = useState(null);
    const [statusMsg, setStatusMsg] = useState("");
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [pendingChanges, setPendingChanges] = useState({});

    async function handleLogin(e) {
        e.preventDefault();
        setError('');
        setStatusMsg('Logging in...');
        
        try {
            const res = await fetch("https://alaqtar-api.visengine.app/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });
            
            if (!res.ok) {
                throw new Error(res.status === 401 ? "Invalid email or password" : "Login failed");
            }
            
            const data = await res.json();
            const accessToken = data.access_token;

            // Create new session with token
            const sessionData = {
                token: accessToken,
                expiry: Date.now() + SESSION_DURATION
            };
            localStorage.setItem('adminSession', JSON.stringify(sessionData));

            setToken(accessToken);
            setStatusMsg("Logged in successfully");
            setError('');
        } catch (err) {
            console.error(err);
            setToken(null);
            setStatusMsg("");
            setError(err.message || "Login failed");
        }
    }

    const handleFilterChange = (filterType, value, checked) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: checked 
                ? [...prev[filterType], value]
                : prev[filterType].filter(item => item !== value)
        }));
    };

    useEffect(() => {
        // Set initial items based on active view and maintain current sort
        let newItems = switchState ? plotData : blockData;
        
        // Apply filters
        if (filters.status.length > 0) {
            newItems = newItems.filter(item => filters.status.includes(item.status));
        }
        if (filters.zone.length > 0) {
            newItems = newItems.filter(item => filters.zone.includes(`zone ${item.zone}`));
        }

        // Sort items
        const sortedItems = [...newItems].sort((a, b) => {
            if (sortConfig.key === '#') {
                const numA = parseInt(a.plot_number || a.block_number);
                const numB = parseInt(b.plot_number || b.block_number);
                return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
            }
            
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        setItems(sortedItems);
    }, [switchState, sortConfig, filters, plotData, blockData]);    

    useEffect(() => {
        // Check for existing session on component mount
        const savedToken = checkSession();
        if (savedToken) {
            setToken(savedToken);
            // Fetch initial data with the restored token
            fetchPlots()
                .then(setPlotData)
                .catch(() => {
                    setPlotData([]);
                    // If data fetch fails with saved token, it might be expired
                    localStorage.removeItem('adminSession');
                    setToken(null);
                });
            fetchBlocks()
                .then(setBlockData)
                .catch(() => {
                    setBlockData([]);
                    localStorage.removeItem('adminSession');
                    setToken(null);
                });
        }

        // Set up interval to check session status
        const intervalId = setInterval(() => {
            const currentToken = checkSession();
            if (!currentToken && token) {
                setToken(null);
                localStorage.removeItem('adminSession');
            }
        }, 60000); // Check every minute

        return () => clearInterval(intervalId);
    }, [token]);

    const handleLogout = () => {
        localStorage.removeItem('adminSession'); // Clear session data
        setToken(null);
    };

    const handleSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        
        const sortedItems = [...items].sort((a, b) => {
            if (key === '#') {
                const numA = parseInt(a.plot_number || a.block_number);
                const numB = parseInt(b.plot_number || b.block_number);
                return direction === 'asc' ? numA - numB : numB - numA;
            }
            
            if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        setItems(sortedItems);
        setSortConfig({ key, direction });
    };

    useEffect(() => {
        fetchPlots()
            .then(setPlotData)
            .catch(() => setPlotData([]));
        fetchBlocks()
            .then(setBlockData)
            .catch(() => setBlockData([]));
    }, []);

    const handleFilter = () => {

        setFilterActive(prev => !prev);
    }

    async function updateBlock(blockId, status) {
        try {
            const res = await fetch(`https://alaqtar-api.visengine.app/blocks/${blockId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status }),
            });
            if (!res.ok) throw new Error("Update failed");
            const data = await res.json();
            setStatusMsg(`Block updated: ${JSON.stringify(data)}`);
        } catch (err) {
            console.error(err);
            setStatusMsg("Failed to update block");
        }
    }

    async function updatePlot(plotId, status) {
        try {
            const res = await fetch(`https://alaqtar-api.visengine.app/plots/${plotId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status }),
            });
            if (!res.ok) throw new Error("Update failed");
            const data = await res.json();
            setStatusMsg(`Block updated: ${JSON.stringify(data)}`);
            return true;
        } catch (err) {
            console.error(err);
            setStatusMsg("Failed to update block");
            return false;
        }
    }

    const handleApplyChanges = async () => {
        if (!hasChanges) return;

        let success = true;
        for (const [id, item] of Object.entries(pendingChanges)) {
            const updateFn = switchState ? updatePlot : updateBlock;
            const result = await updateFn(id, item.status);
            if (!result) {
                success = false;
                break;
            }
        }

        if (success) {
            setStatusMsg("All changes saved successfully");
            setPendingChanges({});
            setHasChanges(false);
            // Refresh data
            if (switchState) {
                fetchPlots().then(setPlotData);
            } else {
                fetchBlocks().then(setBlockData);
            }
        }
    };

    return (
        <>
            {!token ? (
                <div className="admin-login-section">
                    <h1 className="h1">Alaqtar CMS</h1>
                    <form 
                        onSubmit={handleLogin}
                        className='form'
                    >
                        <input
                            type="email" 
                            name="Email"
                            className="input" 
                            placeholder='Email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <div className="password-input-wrapper">
                            <input 
                                type={showPassword ? "text" : "password"}
                                name="password" 
                                className="input"
                                placeholder='Password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button 
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="currentColor"/>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="currentColor"/>
                                        <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button
                            className="submit-btn"
                            type='submit'
                            disabled={statusMsg === 'Logging in...'}
                        >
                            {statusMsg === 'Logging in...' ? 'Logging in...' : 'Submit'}
                        </button>
                    </form>
                </div>
            ) : (
                <div className="admin-panel">
                    <div className="admin-panel-top">

                        <div className="top-left">
                            <img src={Logo} alt="Al'zumuruda Logo" className="logo" />
                            <span>Alaqtar Admin Panel</span>
                        </div>

                        <div className="top-right">
                            <button
                                className="loggout-btn"
                                onClick={handleLogout}>
                                Log Out
                            </button>

                            <Switch2
                                switchChecked={switchState}
                                handleSwitchCheck={setSwitchState}
                                labelText={"Units"}
                                labelText2={"Blocks"}
                            />
                        </div>
                    </div>
                    <div className="table-header">
                        {['#', 'status', 'zone'].map(key => (
                            <div 
                                key={key}
                                className="header-cell" 
                                onClick={() => handleSort(key)}
                            >
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                                <svg 
                                    className='cell-arrow'
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 12 12" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                        transform: `rotate(${sortConfig.key === key && sortConfig.direction === 'asc' ? 180 : 0}deg)`,
                                    }}
                                >
                                    <path d="M6 9L2 5H10L6 9Z" fill="currentColor"/>
                                </svg>
                            </div>
                        ))}
                        <div className="dropdown-filter">
                            <button
                                className="filter-btn"
                                onClick={handleFilter}
                            >
                                Filters
                                <svg 
                                    className='cell-arrow'
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 12 12" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                        transform: `rotate(${filterActive ? 180 : 0}deg)`,
                                    }}
                                >
                                    <path d="M6 9L2 5H10L6 9Z" fill="currentColor"/>
                                </svg>
                            </button>
                            <div className={`filter-menu ${filterActive ? 'active' : ''}`}>
                                <div className="filter-section">
                                    <h3>Status</h3>
                                    {UNIT_STATUSES.map(status => (
                                        <label key={status} className="filter-option">
                                            <input
                                                type="checkbox"
                                                value={status}
                                                onChange={(e) => handleFilterChange('status', status, e.target.checked)}
                                            />
                                            {status}
                                        </label>
                                    ))}
                                </div>
                                <div className="filter-section">
                                    <h3>Zone</h3>
                                    {[...new Set((switchState ? plotData : blockData).map(item => item.zone))].sort((a, b) => a - b).map(zone => (
                                        <label key={zone} className="filter-option">
                                            <input
                                                type="checkbox"
                                                value={`zone ${zone}`}
                                                onChange={(e) => handleFilterChange('zone', `zone ${zone}`, e.target.checked)}
                                            />
                                            Zone {zone}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="table-content">
                        {items.map(item => (
                            <div key={item.id} className="table-row">
                                <div className="table-cell">{item.plot_number || item.block_number}</div>
                                <div className="table-cell status-cell">
                                    <select 
                                        value={pendingChanges[item.id]?.status || item.status}
                                        className="status-select"
                                        onChange={(e) => {
                                            setPendingChanges(prev => ({
                                                ...prev,
                                                [item.id]: {
                                                    ...item,
                                                    status: e.target.value
                                                }
                                            }));
                                            setHasChanges(true);
                                        }}
                                    >
                                        {UNIT_STATUSES.map(status => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="table-cell">{item.zone}</div>
                                {/* <svg 
                                    width="27" 
                                    height="27" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    className='delete-cross'
                                >
                                    <rect x="0" y="10.8" width="27" height="5.4" fill="currentColor"/>
                                    <rect x="10.8" y="0" width="5.4" height="27" fill="currentColor"/>
                                </svg> */}
                            </div>
                        ))}
                    </div>
                    <div className='footer-con'>
                        <button
                            className={`apply-btn ${hasChanges ? "active" : ""}`}
                            onClick={handleApplyChanges}
                            disabled={!hasChanges}
                            style={{
                                transition: 'background-color 0.3s'
                            }}
                        >
                            Apply Changes
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
